import { useQuery } from '@tanstack/react-query';
import service from '../service';
import type { FlowInstance } from '../types/flow';
import type { FlowCategory } from '../types/flowCategory';

/** Largest fish-id list per request; keeps the GET's URL around 1 KB. */
const MAX_FISH_PER_REQUEST = 25;
/** Per-fish loads started within this window share one request. */
const BATCH_WINDOW_MS = 16;
const FISH_FLOW_CATEGORIES_STALE_TIME_MS = 5 * 60 * 1000;
const NO_CATEGORIES: FlowCategory[] = [];

type Waiter = {
  resolve: (categories: FlowCategory[]) => void;
  reject: (error: unknown) => void;
};

const pending = new Map<string, Waiter[]>();
let flushTimer: number | null = null;

/**
 * The live categories of every flow each fish has an instance of, one entry
 * per category, sorted by name. Fish with none are absent from the map.
 */
async function fetchCategoriesByFish(
  fishIds: string[],
): Promise<Map<string, FlowCategory[]>> {
  const response = await service.getFlowInstances(
    {
      'filter[fishId]': fishIds.join(','),
      include: 'flow.category',
      'page[size]': '-1',
    },
    undefined,
    undefined,
    undefined,
  );
  if (!Array.isArray(response?.data)) {
    throw new Error('Failed to load flow instances');
  }

  const byFish = new Map<string, Map<string, FlowCategory>>();
  for (const instance of response.data as FlowInstance[]) {
    const category = instance.flow?.category;
    if (
      !instance.fishId ||
      !category ||
      category.deletedAt ||
      instance.flow?.deletedAt
    ) {
      continue;
    }
    const categories = byFish.get(instance.fishId) ?? new Map();
    categories.set(category.id, category);
    byFish.set(instance.fishId, categories);
  }

  return new Map(
    [...byFish].map(([fishId, categories]) => [
      fishId,
      [...categories.values()].sort((a, b) => a.name.localeCompare(b.name)),
    ]),
  );
}

function flush() {
  flushTimer = null;
  const batch = new Map(pending);
  pending.clear();

  const fishIds = [...batch.keys()];
  for (let start = 0; start < fishIds.length; start += MAX_FISH_PER_REQUEST) {
    const chunk = fishIds.slice(start, start + MAX_FISH_PER_REQUEST);
    fetchCategoriesByFish(chunk).then(
      (byFish) =>
        chunk.forEach((fishId) =>
          batch
            .get(fishId)
            ?.forEach((waiter) =>
              waiter.resolve(byFish.get(fishId) ?? NO_CATEGORIES),
            ),
        ),
      (error) =>
        chunk.forEach((fishId) =>
          batch.get(fishId)?.forEach((waiter) => waiter.reject(error)),
        ),
    );
  }
}

function loadFishCategories(fishId: string): Promise<FlowCategory[]> {
  return new Promise((resolve, reject) => {
    const waiters = pending.get(fishId) ?? [];
    waiters.push({ resolve, reject });
    pending.set(fishId, waiters);
    flushTimer ??= window.setTimeout(flush, BATCH_WINDOW_MS);
  });
}

/**
 * Flow categories for one fish, cached per fish. Loads started in the same
 * frame by different callers are coalesced into one
 * `GET /flow/instances?filter[fishId]=…` of at most MAX_FISH_PER_REQUEST ids.
 * Once loaded, the cached categories are returned even while `enabled` is false.
 */
export function useFishFlowCategories(
  fishId: string | null,
  enabled: boolean,
): FlowCategory[] {
  const { data } = useQuery({
    queryKey: ['fishFlowCategories', fishId],
    queryFn: () => loadFishCategories(fishId as string),
    enabled: enabled && !!fishId,
    staleTime: FISH_FLOW_CATEGORIES_STALE_TIME_MS,
    retry: false,
  });

  return data ?? NO_CATEGORIES;
}
